import { cellRawValue, type DeleteOption, type EditOption } from './grid.models';

export interface OptionRestrictionResult {
  allow: boolean;
  alertProperty?: string;
  alertMessage?: string;
}

/** Shared by single delete, bulk delete, and edit restrictions — same rule shape as `EditOption`/`DeleteOption`. */
export function evaluateOptionRestrictions(
  entity: object,
  options: (EditOption | DeleteOption)[]
): OptionRestrictionResult {
  let totalAccept = true;
  let alertProperty = '';
  let alertMessage = '';

  for (const option of options) {
    if (!option.objectProperty) {
      continue;
    }

    // e.g. entity.approved — compared against the configured blocked value(s).
    const data = cellRawValue(entity, option.objectProperty);
    const valueType = typeof option.objectValue;
    let accept = true;

    // Single blocked value: action is allowed only when the field does not match
    // (e.g. approved !== 'YES' → delete permitted).
    if (valueType === 'string' || valueType === 'boolean') {
      accept = data !== option.objectValue;
    } else if (Array.isArray(option.objectValue)) {
      // Multiple blocked values: deny when the field equals any listed value
      // (e.g. edispatch_status in ['SEND_GIB_SUCCEED', 'APPROVED']).
      for (const value of option.objectValue) {
        if (value === data) {
          accept = false;
        }
      }
    }

    if (!accept) {
      // Keep the last failing rule's message; all rules must pass (AND).
      alertProperty = option.alertProperty ?? '';
      alertMessage = option.alertMessage ?? '';
    }
    totalAccept = totalAccept && accept;
  }

  return {
    allow: totalAccept,
    alertProperty: alertProperty || undefined,
    alertMessage: alertMessage || undefined,
  };
}

/** User-facing error text for a blocked delete/edit — mirrors legacy kl-grid message shape. */
export function restrictionErrorMessage(record: object, restriction: OptionRestrictionResult): string {
  // Optional prefix from another field (alertProperty) before the i18n alertMessage key/text.
  if (restriction.alertProperty) {
    return String(cellRawValue(record, restriction.alertProperty) ?? '') + (restriction.alertMessage ?? '');
  }
  return restriction.alertMessage ?? 'error_data';
}
